import { useState, useCallback, useEffect } from 'react';
import { getPromoStorageKey, setPromoDismissed, activateChip } from '../../constants/promotionKeys';

interface UseBannerDismissOptions {
  chipId?: string;
  exitDurationMs?: number;
}

interface UseBannerDismissReturn {
  isDismissed: boolean;
  isExiting: boolean;
  dismiss: () => void;
}

export function useBannerDismiss(
  id: string,
  options: UseBannerDismissOptions = {}
): UseBannerDismissReturn {
  const { chipId, exitDurationMs = 200 } = options;

  const [isDismissed, setIsDismissed] = useState(
    () => sessionStorage.getItem(getPromoStorageKey(id)) === '1'
  );
  const [isExiting, setIsExiting] = useState(false);

  const dismiss = useCallback(() => {
    setIsExiting(true);
    setTimeout(() => {
      setPromoDismissed(id);
      if (chipId) activateChip(chipId);
      setIsDismissed(true);
      setIsExiting(false);
    }, exitDurationMs);
  }, [id, chipId, exitDurationMs]);

  useEffect(() => {
    const handleReset = (e: CustomEvent<{ cardId: string }>) => {
      if (e.detail.cardId === id) {
        setIsDismissed(false);
        setIsExiting(false);
      }
    };
    window.addEventListener('jigsaw:promo:infocard:reset', handleReset as EventListener);
    return () => window.removeEventListener('jigsaw:promo:infocard:reset', handleReset as EventListener);
  }, [id]);

  useEffect(() => {
    const handleExternalDismiss = (e: CustomEvent<{ id: string }>) => {
      if (e.detail.id === id) {
        setIsDismissed(true);
        setIsExiting(false);
      }
    };
    window.addEventListener('jigsaw:promo:dismiss', handleExternalDismiss as EventListener);
    return () => window.removeEventListener('jigsaw:promo:dismiss', handleExternalDismiss as EventListener);
  }, [id]);

  return { isDismissed, isExiting, dismiss };
}
